import React, { useState, useRef } from 'react';
import { Box, Button, Dialog, DialogTitle, Typography, Grid, Divider } from '@mui/material';
import { Helmet } from 'react-helmet';
import '../Spinner.css';

const SECTIONS = [
  { label: 'Truth', color: 'violet' },
  { label: 'Dare', color: 'indigo' },
  { label: 'Truth', color: 'blue' },
  { label: 'Dare', color: 'green' },
  { label: 'Truth', color: 'yellow' },
  { label: 'Dare', color: 'orange' },
  { label: 'Truth', color: 'red' },
  { label: 'Dare', color: 'violet' },
  { label: 'Truth', color: 'indigo' },
  { label: 'Dare', color: 'blue' },
  { label: 'Truth', color: 'green' },
  { label: 'Dare', color: 'yellow' },
];

export default function TruthOrDareSpinner() {
  const [rotation, setRotation] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [showResult, setShowResult] = useState(false);

  const [truthInput, setTruthInput] = useState('');
  const [dareInput, setDareInput] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');

  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const [promptType, setPromptType] = useState('truths');
  const [truths, setTruths] = useState(() => JSON.parse(localStorage.getItem('truths') || '[]'));
  const [dares, setDares] = useState(() => JSON.parse(localStorage.getItem('dares') || '[]'));

  const wheelRef = useRef(null);

  const startRotation = () => {
    if (!wheelRef.current) return;

    // Reset current transform to prevent transition stacking
    wheelRef.current.style.transition = 'none';
    wheelRef.current.style.transform = `rotate(${rotation % 360}deg)`;
    void wheelRef.current.offsetHeight;

    // Generate random spin degrees
    const spinDegrees = 360 * 5 + Math.floor(Math.random() * 360);
    setRotation(spinDegrees);

    // Apply spinning animation
    wheelRef.current.style.transition = 'transform 4s cubic-bezier(0.25, 1, 0.5, 1)';
    wheelRef.current.style.transform = `rotate(${spinDegrees}deg)`;

    // After spin completes, calculate result
    setTimeout(() => {
      const normalizedDegrees = spinDegrees % 360;
      const segmentAngle = 360 / SECTIONS.length;
      const adjustedDegrees = (normalizedDegrees + 90) % 360;
      const index = Math.floor(adjustedDegrees / segmentAngle);

      const label = SECTIONS[index].label;
      setSelectedLabel(label);

      // Pick random prompt from localStorage
      const prompts = JSON.parse(localStorage.getItem(label.toLowerCase() + 's') || '[]');
      const randomPrompt = prompts.length > 0 ? prompts[Math.floor(Math.random() * prompts.length)] : 'No prompts added yet.';
      setSelectedPrompt(randomPrompt);

      setShowResult(true);
    }, 4000);

  };

  const refreshPrompts = () => {
    setTruths(JSON.parse(localStorage.getItem('truths') || '[]'));
    setDares(JSON.parse(localStorage.getItem('dares') || '[]'));
  };

  const handleAddPrompt = () => {
    if (!promptInput.trim()) return;
    const updated = [...(promptType === 'truths' ? truths : dares), promptInput.trim()];
    localStorage.setItem(promptType, JSON.stringify(updated));
    setPromptInput('');
    refreshPrompts();
  };

  const handleDelete = (type, index) => {
    const list = JSON.parse(localStorage.getItem(type) || '[]');
    list.splice(index, 1);
    localStorage.setItem(type, JSON.stringify(list));
    refreshPrompts();
  };

  const handleEdit = (type, index, newText) => {
    const list = JSON.parse(localStorage.getItem(type) || '[]');
    list[index] = newText;
    localStorage.setItem(type, JSON.stringify(list));
    refreshPrompts();
  };

  return (
    <>
      <Helmet>
        <title>Truth or Dare Spinner Game - Fun Party Game</title>
        <meta
          name="description"
          content="Play the ultimate Truth or Dare spinner game online. Add custom prompts, spin the wheel, and have fun with friends!"
        />
        <meta
          name="keywords"
          content="truth or dare, spinner game, party game, online spinner, custom truth or dare"
        />
        <link rel="canonical" href="https://timepassgame.com/spin-wheels/truth-or-dare-spinner" />
      </Helmet>
      
      <Box className="spinner-container">
        {/* Wheel Container with Triangle and Wheel */}
        <Box className="wheel-container">
          {/* Triangle Pointer */}
          <Box className="triangle-down" />

          {/* Spinner Wheel */}
          <Box
            component="ul"
            ref={wheelRef}
            className="circle"
            sx={{ transition: 'transform 0s' }}
          >
            {SECTIONS.map((item, index) => (
              <li
                key={index}
                className="wheel-part"
                style={{
                  transform: `rotate(${index * 30}deg) skewY(-60deg)`,
                }}
              >
                <Box
                  className="text"
                  sx={{
                    backgroundColor: item.color,
                    color: 'black',
                  }}
                >
                  {item.label}
                </Box>
              </li>
            ))}
          </Box>
        </Box>

        <Typography
          variant="body1"
          onClick={() => setManageDialogOpen(true)}
          sx={{
            mb: 1,
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'white',
            '&:hover': {
              textDecoration: 'none',
              color: '#ccc',
            },
          }}
        >
          Manage Truth/Dare Prompts
        </Typography>

        {/* Spin Button */}
        <Button
          variant="contained"
          size="large"
          onClick={startRotation}
          sx={{
            background: 'linear-gradient(to right, #4facfe, #00f2fe)',
            borderRadius: '50px',
            px: 6,
            py: 1.5,
            fontWeight: 'bold',
            boxShadow: 3,
            '&:hover': {
              background: 'linear-gradient(to right, #00f2fe, #4facfe)',
            },
          }}
        >
          SPIN
        </Button>

        {/* Result Dialog */}
        <Dialog open={showResult} onClose={() => setShowResult(false)}>
          <DialogTitle sx={{ fontSize: '22px', fontWeight: 'bold', textAlign: 'center' }}>
            🎉 You got: {selectedLabel}!
            <br />
            ➤ {selectedPrompt}
          </DialogTitle>
        </Dialog>

        <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Manage Prompts</DialogTitle>
          <Box sx={{ p: 2 }}>
            {/* Add Form */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <select value={promptType} onChange={(e) => setPromptType(e.target.value)}>
                <option value="truths">Truth</option>
                <option value="dares">Dare</option>
              </select>
              <input
                type="text"
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                placeholder={`Enter a ${promptType === 'truths' ? 'Truth' : 'Dare'}`}
                style={{ flexGrow: 1 }}
              />
              <Button variant="contained" onClick={handleAddPrompt}>Add</Button>
            </Box>

            {/* List of Prompts */}
            <Box>
              <Grid container spacing={4}>
                {/* Truths Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 1, borderBottom: '2px solid #1976d2' }}>
                    Truths
                  </Typography>
                  {truths.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No truths added yet.</Typography>
                  ) : (
                    truths.map((t, i) => (
                      <PromptItem
                        key={i}
                        type="truths"
                        index={i}
                        text={t}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                      />
                    ))
                  )}
                </Grid>

                {/* Dares Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 1, borderBottom: '2px solid #ff5722' }}>
                    Dares
                  </Typography>
                  {dares.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No dares added yet.</Typography>
                  ) : (
                    dares.map((d, i) => (
                      <PromptItem
                        key={i}
                        type="dares"
                        index={i}
                        text={d}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                      />
                    ))
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </>
  );
}

const PromptItem = ({ type, index, text, handleEdit, handleDelete }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedText, setEditedText] = useState(text);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {editMode ? (
        <>
          <input
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            style={{ flexGrow: 1 }}
          />
          <Button
            size="small"
            onClick={() => {
              handleEdit(type, index, editedText);
              setEditMode(false);
            }}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Typography
            sx={{
              flexGrow: 1,
              textAlign: 'left',
              wordBreak: 'break-word',
              fontSize: '15px',
            }}
          >
            {text}
          </Typography>
          <Button size="small" onClick={() => setEditMode(true)}>Edit</Button>
        </>
      )}
      <Button size="small" color="error" onClick={() => handleDelete(type, index)}>Delete</Button>
    </Box>
  );
};
